import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../../utils/functions/api';
import * as actions from '../../../redux/actions';

// ปุ่ม "สร้างโครงการ" — โผล่เฉพาะ opportunity ที่จบงานแล้ว (WON) ใช้ ProjectForm
// (components/Forms) ตัวเดียวกับหน้า NewProject.js/EditProject.js เป๊ะ แค่ prefill
// project_name/มูลค่างาน/ผู้ว่าจ้างจาก opportunity ให้ก่อน ที่เหลือ user กรอกเอง
// บันทึกเสร็จปิด modal เลย ไม่ redirect ไปหน้า EditProject เต็มจออีกต่อไป — popup
// นี้มีฟิลด์ครบพอสร้างโครงการได้ในตัวอยู่แล้ว ไปแก้ไขเพิ่มทีหลังได้จากหน้า Project ปกติ
export function useCreateProject({ opportunity, showSnack }) {
  const dispatch = useDispatch();
  const contact = useSelector((state) => state.contact);

  const form = useForm();
  const { reset, setValue } = form;

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const [dateEnd, setDateEnd] = useState(new Date());
  const [includeVAT, setIncludeVAT] = useState(false);
  const [cost, setCost] = useState(0);
  const [extraGpsSites, setExtraGpsSites] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [saving, setSaving] = useState(false);

  const openDialog = () => setOpen(true);
  const closeDialog = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    reset({
      name: opportunity.project_name,
      customer: opportunity.contact_name_snapshot || '',
      client_id: opportunity.contact_id || null,
    });
    setCost(Number(opportunity.value) || 0);
    setDate(new Date());
    setDateEnd(new Date());
    setIncludeVAT(false);
    setExtraGpsSites([]);
    setSelectedClient(
      opportunity.contact_id
        ? { _id: opportunity.contact_id, name: opportunity.contact_name_snapshot || '' }
        : null,
    );
    dispatch(actions.contactAll({ contactType: 'client', size: 50, page: 1 }));

    // โหลดพนักงานทั้งหมดสำหรับ dropdown "Project Manager" — pattern เดียวกับ
    // NewProject.js/EditProject.js (ยังไม่มี redux action สำเร็จรูปสำหรับดึงทั้งหมด)
    const fetchAllEmployees = async () => {
      const PAGE_SIZE = 50;
      let pg = 1;
      let accumulated = [];
      let hasMore = true;
      while (hasMore) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get(
          `${process.env.REACT_APP_API_URL}/employee?name=&size=${PAGE_SIZE}&page=${pg}&activeOnly=false`,
        );
        const rows = data?.rows || [];
        const total = data?.total || 0;
        accumulated = [...accumulated, ...rows];
        setAllEmployees([...accumulated]);
        hasMore = accumulated.length < total && rows.length > 0;
        pg += 1;
      }
    };
    fetchAllEmployees();
  }, [open, opportunity, reset, dispatch]);

  const contacts = contact?.rows || [];

  const submit = async (data) => {
    setSaving(true);
    try {
      const gpsSitesPayload = extraGpsSites
        .map((s) => ({
          name: (s.name || '').trim(),
          lat: s.lat !== '' && s.lat != null ? parseFloat(s.lat) : NaN,
          lon: s.lon !== '' && s.lon != null ? parseFloat(s.lon) : NaN,
        }))
        .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon));

      const latRaw = data?.gps?.lat ?? data['gps.lat'];
      const lonRaw = data?.gps?.lon ?? data['gps.lon'];
      const lat = latRaw !== '' && latRaw != null ? parseFloat(latRaw) : NaN;
      const lon = lonRaw !== '' && lonRaw != null ? parseFloat(lonRaw) : NaN;
      const gpsPayload = Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : undefined;

      const createdProject = await dispatch(actions.projectCreate({
        ...data,
        date_start: date,
        date_end: dateEnd,
        cost,
        vat: includeVAT ? 0 : cost * 0.07,
        include_vat: includeVAT,
        gps: gpsPayload,
        gps_sites: gpsSitesPayload,
        opportunity_id: opportunity.id,
      }));

      // เขียน project_id กลับเข้า opportunity ต้นทางทันที ไม่ block การปิด modal/แจ้งเตือนสำเร็จ
      // (โครงการสร้างสำเร็จแล้วเป็นเรื่องหลัก การ link กลับเป็นแค่ metadata เสริมให้ตาราง
      // OpportunitiesTable.js โชว์ได้ — พลาดได้โดยไม่กระทบผู้ใช้ตรงๆ)
      if (createdProject?._id) {
        dispatch(actions.linkOpportunityProject(opportunity.id, createdProject._id)).catch(
          (err) => console.error('link opportunity to project failed', err),
        );
      }

      showSnack('สร้างโครงการสำเร็จ');
      closeDialog();
    } catch (err) {
      showSnack(err.message || 'สร้างโครงการไม่สำเร็จ', 'error');
    } finally {
      setSaving(false);
    }
  };

  return {
    open,
    openDialog,
    closeDialog,
    form,
    date,
    setDate,
    dateEnd,
    setDateEnd,
    includeVAT,
    setIncludeVAT,
    cost,
    setCost,
    extraGpsSites,
    setExtraGpsSites,
    allEmployees,
    contacts,
    selectedClient,
    setSelectedClient,
    setValue,
    saving,
    submit,
  };
}

export default useCreateProject;
